import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { leadsAPI, customersAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, UserPlus, Search, Clock, Plus } from 'lucide-react';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const emptyLead = {
  name: '',
  phone: '',
  email: '',
  address: '',
  inquiry_type: 'quote',
  message: '',
  referred_by: ''
};

export default function Leads() {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sameBillingAddress, setSameBillingAddress] = useState(true);
  const [leadFormData, setLeadFormData] = useState(emptyLead);
  const [convertFormData, setConvertFormData] = useState({
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    cost_per_service: '',
    billing_plan: 'per-cut',
    service_frequency: 'weekly',
    contract_start_date: '',
    contract_end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [leadsRes, customersRes] = await Promise.all([
        leadsAPI.getAll(params),
        customersAPI.getAll()
      ]);
      setLeads(leadsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    
    if (!leadFormData.name || !leadFormData.phone) {
      toast.error('Please enter name and phone number');
      return;
    }

    try {
      // Convert empty strings to null for optional fields
      const dataToSend = {
        ...leadFormData,
        email: leadFormData.email || null,
        address: leadFormData.address || null,
        message: leadFormData.message || null,
        referred_by: leadFormData.referred_by || null
      };
      
      await leadsAPI.create(dataToSend);
      toast.success('Lead added successfully');
      setAddDialogOpen(false);
      setLeadFormData(emptyLead);
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        toast.error(errorDetail.map(e => e.msg || e).join(', '));
      } else {
        toast.error('Failed to add lead');
      }
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadsAPI.update(leadId, { status: newStatus });
      toast.success('Lead updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleConvert = async () => {
    if (!convertFormData.street_address || !convertFormData.city || !convertFormData.state || !convertFormData.zip_code || !convertFormData.cost_per_service) {
      toast.error('Please fill in all required address and cost fields');
      return;
    }

    try {
      await leadsAPI.convert(selectedLead.id, {
        contact_name: selectedLead.name,
        email: selectedLead.email || '',
        phone: selectedLead.phone,
        street_address: convertFormData.street_address,
        city: convertFormData.city,
        state: convertFormData.state,
        zip_code: convertFormData.zip_code,
        billing_street: sameBillingAddress ? convertFormData.street_address : convertFormData.billing_street,
        billing_city: sameBillingAddress ? convertFormData.city : convertFormData.billing_city,
        billing_state: sameBillingAddress ? convertFormData.state : convertFormData.billing_state,
        billing_zip: sameBillingAddress ? convertFormData.zip_code : convertFormData.billing_zip,
        preferred_communication: 'phone',
        preferred_days: [],
        service_frequency: convertFormData.service_frequency,
        cost_per_service: parseFloat(convertFormData.cost_per_service),
        billing_plan: convertFormData.billing_plan,
        contract_start_date: convertFormData.contract_start_date || null,
        contract_end_date: convertFormData.contract_end_date || null,
        referred_by: selectedLead.referred_by || null,
        status: 'active'
      });
      
      toast.success('Lead converted to customer!');
      setConvertDialogOpen(false);
      setSelectedLead(null);
      fetchData();
    } catch (error) {
      console.error('Convert error:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  const parseAddressFromLead = (address) => {
    if (!address) return { street: '', city: '', state: '', zip: '' };
    
    const parts = address.split(',').map(p => p.trim());
    if (parts.length >= 3) {
      const lastPart = parts[parts.length - 1].trim();
      const stateZipMatch = lastPart.match(/([A-Z]{2})\s*(\d{5})?/);
      
      return {
        street: parts[0] || '',
        city: parts[1] || '',
        state: stateZipMatch ? stateZipMatch[1] : '',
        zip: stateZipMatch && stateZipMatch[2] ? stateZipMatch[2] : ''
      };
    }
    
    return { street: address, city: '', state: '', zip: '' };
  };

  const openConvertDialog = (lead) => {
    setSelectedLead(lead);
    const parsed = parseAddressFromLead(lead.address);
    setConvertFormData({
      street_address: parsed.street,
      city: parsed.city,
      state: parsed.state,
      zip_code: parsed.zip,
      billing_street: '',
      billing_city: '',
      billing_state: '',
      billing_zip: '',
      cost_per_service: '',
      billing_plan: 'per-cut',
      service_frequency: 'weekly',
      contract_start_date: '',
      contract_end_date: ''
    });
    setSameBillingAddress(true);
    setConvertDialogOpen(true);
  };

  const getInquiryLabel = (type) => {
    const labels = {
      'curious': 'Just Curious',
      'quote': 'Get a Quote',
      'talk_options': 'Discuss Options'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'status-new',
      'contacted': 'status-contacted',
      'converted': 'status-converted',
      'closed': 'bg-gray-200 text-gray-600'
    };
    return colors[status] || 'bg-gray-200 text-gray-600';
  };

  const filteredLeads = leads.filter(l =>
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.phone.includes(search)
  );

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500 mt-1">Manage incoming inquiries</p>
        </div>
        
        {/* Add Lead Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={(open) => {
          setAddDialogOpen(open);
          if (!open) setLeadFormData(emptyLead);
        }}>
          <DialogTrigger asChild>
            <Button className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="add-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddLead} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Name *</Label>
                  <Input
                    value={leadFormData.name}
                    onChange={(e) => setLeadFormData({...leadFormData, name: e.target.value})}
                    placeholder="John Smith"
                    data-testid="lead-name-input"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={leadFormData.phone}
                    onChange={(e) => setLeadFormData({...leadFormData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    data-testid="lead-phone-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={leadFormData.email}
                    onChange={(e) => setLeadFormData({...leadFormData, email: e.target.value})}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div>
                <Label>Service Address</Label>
                <Input
                  value={leadFormData.address}
                  onChange={(e) => setLeadFormData({...leadFormData, address: e.target.value})}
                  placeholder="123 Main St, City, State 12345"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Inquiry Type</Label>
                  <Select value={leadFormData.inquiry_type} onValueChange={(v) => setLeadFormData({...leadFormData, inquiry_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="curious">Just Curious</SelectItem>
                      <SelectItem value="quote">Get a Quote</SelectItem>
                      <SelectItem value="talk_options">Discuss Options</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Referred By (Optional)</Label>
                  <Select 
                    value={leadFormData.referred_by || "none"} 
                    onValueChange={(v) => setLeadFormData({...leadFormData, referred_by: v === "none" ? "" : v})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select referrer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No referral</SelectItem>
                      {Array.isArray(customers) && customers.filter(c => c.status === 'active').map(c => (
                        <SelectItem key={c.id} value={c.contact_name}>{c.contact_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Message / Notes</Label>
                <Textarea
                  value={leadFormData.message}
                  onChange={(e) => setLeadFormData({...leadFormData, message: e.target.value})}
                  placeholder="Any additional details..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-600">
                <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="lead-submit-btn">
                  Add Lead
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search leads..."
                className="pl-10"
                data-testid="lead-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads List */}
      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse" />)}
        </div>
      ) : filteredLeads.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">No leads found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredLeads.map((lead) => (
            <Card key={lead.id} className="border-0 shadow-sm" data-testid={`lead-card-${lead.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-gray-900">{lead.name}</h3>
                      <span className={`status-badge ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                        {getInquiryLabel(lead.inquiry_type)}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${lead.phone}`} className="hover:text-lime-600">{lead.phone}</a>
                      </span>
                      {lead.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {lead.email}
                        </span>
                      )}
                      {lead.address && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {lead.address}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(lead.created_at).toLocaleString()}
                      </span>
                    </div>

                    {lead.referred_by && (
                      <p className="text-sm text-lime-600 mb-2">
                        📢 Referred by: {lead.referred_by}
                      </p>
                    )}

                    {lead.message && (
                      <div className="p-3 bg-gray-800 border border-gray-700 rounded-lg">
                        <p className="text-sm text-gray-300">{lead.message}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {lead.status === 'new' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange(lead.id, 'contacted')}
                      >
                        Mark Contacted
                      </Button>
                    )}
                    {lead.status !== 'converted' && lead.status !== 'closed' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-lime-500 hover:bg-lime-600 text-black"
                          onClick={() => openConvertDialog(lead)}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Convert
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusChange(lead.id, 'closed')}
                        >
                          Close
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Convert Lead to Customer</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6 mt-4">
              <div className="p-4 bg-gray-800 border border-gray-600 rounded-lg">
                <p className="font-medium text-gray-100">{selectedLead.name}</p>
                <p className="text-sm text-gray-400">{selectedLead.phone}</p>
                {selectedLead.email && <p className="text-sm text-gray-400">{selectedLead.email}</p>}
                {selectedLead.referred_by && <p className="text-sm text-lime-500">Referred by: {selectedLead.referred_by}</p>}
              </div>

              {/* Service Address */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lime-500 border-b border-gray-600 pb-2">Service Address</h3>
                <div>
                  <Label>Street Address *</Label>
                  <Input
                    value={convertFormData.street_address}
                    onChange={(e) => setConvertFormData({...convertFormData, street_address: e.target.value})}
                    placeholder="123 Main Street"
                    data-testid="convert-street-input"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label>City *</Label>
                    <Input
                      value={convertFormData.city}
                      onChange={(e) => setConvertFormData({...convertFormData, city: e.target.value})}
                      placeholder="City"
                      data-testid="convert-city-input"
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Select value={convertFormData.state} onValueChange={(v) => setConvertFormData({...convertFormData, state: v})}>
                      <SelectTrigger data-testid="convert-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                      <SelectContent>
                        {US_STATES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>ZIP Code *</Label>
                    <Input
                      value={convertFormData.zip_code}
                      onChange={(e) => setConvertFormData({...convertFormData, zip_code: e.target.value})}
                      placeholder="12345"
                      data-testid="convert-zip-input"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-gray-600 pb-2">
                  <h3 className="font-semibold text-lime-500">Billing Address</h3>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <Checkbox 
                      checked={sameBillingAddress} 
                      onCheckedChange={(checked) => setSameBillingAddress(checked)}
                    />
                    Same as service address
                  </label>
                </div>
                
                {!sameBillingAddress && (
                  <>
                    <div>
                      <Label>Street Address</Label>
                      <Input
                        value={convertFormData.billing_street}
                        onChange={(e) => setConvertFormData({...convertFormData, billing_street: e.target.value})}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <Label>City</Label>
                        <Input
                          value={convertFormData.billing_city}
                          onChange={(e) => setConvertFormData({...convertFormData, billing_city: e.target.value})}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <Label>State</Label>
                        <Select value={convertFormData.billing_state} onValueChange={(v) => setConvertFormData({...convertFormData, billing_state: v})}>
                          <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>ZIP Code</Label>
                        <Input
                          value={convertFormData.billing_zip}
                          onChange={(e) => setConvertFormData({...convertFormData, billing_zip: e.target.value})}
                          placeholder="12345"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Service & Billing Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lime-500 border-b border-gray-600 pb-2">Service & Billing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Service Frequency</Label>
                    <Select 
                      value={convertFormData.service_frequency} 
                      onValueChange={(v) => setConvertFormData({...convertFormData, service_frequency: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="one-time">One-Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Billing Plan</Label>
                    <Select 
                      value={convertFormData.billing_plan} 
                      onValueChange={(v) => setConvertFormData({...convertFormData, billing_plan: v})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per-cut">Per Cut</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Cost per Service *</Label>
                  <Input
                    type="number"
                    value={convertFormData.cost_per_service}
                    onChange={(e) => setConvertFormData({...convertFormData, cost_per_service: e.target.value})}
                    placeholder="50"
                    data-testid="convert-cost-input"
                    className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>

                {/* Contract Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contract Start Date</Label>
                    <Input
                      type="date"
                      value={convertFormData.contract_start_date}
                      onChange={(e) => setConvertFormData({...convertFormData, contract_start_date: e.target.value})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Label>Contract End Date</Label>
                    <Input
                      type="date"
                      value={convertFormData.contract_end_date}
                      onChange={(e) => setConvertFormData({...convertFormData, contract_end_date: e.target.value})}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-600">
                <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-lime-500 hover:bg-lime-600 text-black"
                  onClick={handleConvert}
                  data-testid="convert-submit-btn"
                >
                  Convert to Customer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
